/* eslint-disable react-hooks/exhaustive-deps */
import DataSet from "@antv/data-set";
import { Chart } from "@antv/g2";
import { useEffect, useMemo, useRef, useState } from "react";
import moment from 'moment';
import { TooltipWithColumns } from "../../../../helpers/Charts";

const tooltipConfig = [
  {
    children: {
      name: {
        style: "width: 100px",
        type: 'text',
        childStyle: "width: 90px;",
      },
    },
  },
  {
    children: {
      lastUpdated: {
        style: "width: 100px",
        type: 'text',
        childStyle: "width: 90px;"
      },
    },
  },
  {
    children: {
      progress: {
        style: "width: 111px",
        type: 'progress',
      },
    }
  },
];

const Y_LIMIT = 14;
const X_CHAR_SIZE = 8;

export const locationsColors = {
  '5': '#172A3C',
  '4': '#455A6F',
  '3': '#5E748A',
  '2': '#91ABC3',
  '1': '#C8E5FF',
};

export const stakeholdersColors = {
  '5': '#2E90FA',
  '4': '#64A4FF',
  '3': '#8FB8FF',
  '2': '#B3CDFF',
  '1': '#D4E2FF',
};

export function useChartHelper({
  data,
  type,
  resizeTrigger,
  t = (s) => s,
}) {
  const container = useRef();
  const [graph, setGraph] = useState(null);

  const d = useMemo(() => {
    if (data) {
      const brands = { 1: null, 2: null, 3: null, 4: null, 5: null };

      data.forEach((d) => {
        let progress = 0;

        if (Array.isArray(d.identifiedBy)) {
          d.identifiedBy.forEach((iB) => progress += iB.progress);
          progress = progress / d.identifiedBy.length;
        }

        const sourcesVal = d.sources * d.sources * (d.sources + 1);

        const valToPush = {
          name: { label: d.name, brand: d.brand },
          identifiedBy: d.identfiedBy,
          value: (sourcesVal * (progress === 0 ? 1 : progress)),
        }
        if (brands[d.brand]) {
          brands[d.brand].push(valToPush);
        } else {
          brands[d.brand] = [valToPush];
        }
      });

      const dataToReturn = [];

      Object.keys(brands).forEach((k) => {
        if (brands[k] !== null) {
          dataToReturn.push({
            brand: k.toString(),
            children: brands[k],
          });
        }
      });

      return dataToReturn;
    }

    return null;
  }, [data]);

  const nodesData = useMemo(() => {
    if (d) {
      const { DataView } = DataSet;

      d.forEach((v) => {
        v.value = null;
      });

      const data = {
        name: 'root',
        children: d,
      };

      const dv = new DataView();

      dv.source(data, {
        type: 'hierarchy',
      }).transform({
        field: 'value',
        type: 'hierarchy.treemap',
        tile: 'treemapResquarify',
        as: ['x', 'y'],
      });

      const nodes = [];

      for (const node of dv.getAllNodes()) {
        if (!node.children) {
          const eachNode = {
            name: node.data.name,
            x: node.x,
            y: node.y,
            depth: node.depth,
            value: node.value,
            identifiedBy: node.data.identifiedBy,
          };
          if (!node.data.brand && node.parent) {
            eachNode.brand = node.parent.data.brand;
          } else {
            eachNode.brand = node.data.brand;
          }
          nodes.push(eachNode);
        }
      }

      return nodes;
    }
  }, [d]);

  const updateChart = (chart) => {
    chart.coordinate().scale(1, -1);
    chart.data(nodesData);
    chart.axis(false);
    chart.legend(false);
    chart.animate(false);
    chart.tooltip({
      customContent: (_, data) => {
        const color = data[0] ? data[0].color : '';
        const title = (data[0] && data[0].data) ? data[0].data.name.label : '';
        const dd = (data[0] && data[0].data && data[0].data.identifiedBy) ? data[0].data.identifiedBy.map((d) => ({
          name: d.name,
          lastUpdated: moment(d.lastUpdated).format('DD MMM YYYY'),
          progress: d.progress,
        })) : [];

        const tooltipWithColumns = new TooltipWithColumns(dd, tooltipConfig);

        return `
          <div style="padding: 12px; background: white; min-width: 310px; border-radius: 4px;">
            <div style="margin-bottom: 12px; display: flex; flex-direction: row;">
              <div>
                <div style="width: 5px; height: 5px; border-radius: 100%; background: ${color}; margin-top: 5px"></div>
              </div>
              <h2 style="margin-bottom: 0px; width: 200px; margin-left: 6px; font-size: 14px; font-weight: 700; color: black">
                ${title}
              </h2>
            </div>
            <div style="display: flex; flex-direction: column;">
              <div style="display: flex;">
                <div style="width: 100px;"><label style="font-size: 10px; color: #2E90FA; font-weight: 700">${t('Identified by')}</label></div>
                <div style="width: 100px;"><label style="font-size: 10px; color: #2E90FA; font-weight: 700">${t('Last Updated')}</label></div>
                <div style="width: 110px;"><label style="font-size: 10px; color: #2E90FA; font-weight: 700">${t('Assessment')}</label></div>
              </div>
              <div style="border-bottom: 1px solid #CCCCCC;"></div>
              ${tooltipWithColumns.rows()}
            </div>
          </div>
        `;

      },
    });

    chart
      .polygon()
      .position('x*y')
      .color('brand')
      .style({
        lineWidth: 1,
        stroke: '#fff',
      })
      .label(
        'name',
        (config) => ({
          offset: 0,
          style: {
            fontSize: 9,
            fill: 'white',
            shadowBlur: 10,
          },
          layout: {
            type: 'limit-in-shape'
          },
          content: (a, b) => {
            const x = b.x[1] - b.x[0];
            const y = b.y[1] - b.y[3];
            const name = a.name.label;

            if (y < Y_LIMIT) {
              return '';
            }

            if (name) {
              if (name.length * X_CHAR_SIZE > x) {
                for (let i = name.length - 1; i >= 1; i -= 1) {
                  let substring = `${name.substring(0, i)}...`;
                  if (substring.length * X_CHAR_SIZE < x) {
                    return substring;
                  }
                }
                return '';
              }
            }

            if (x > 32) {
              return `${config.label}`;
            }
            return config.label;
          },
        }),
      );
    // chart.interaction('element-active');
    chart.render();
    setGraph(chart);
  };

  const createChart = () => {
    const colors10 = [];
    if (d) {
      d.forEach((i) => {
        if (type === 'location' || type === 'mineSite') {
          if (locationsColors[i.brand]) {
            colors10.push(locationsColors[i.brand]);
          }
        } else if (type === 'stakeholder' || type === 'supplyChainActor') {
          if (stakeholdersColors[i.brand]) {
            colors10.push(stakeholdersColors[i.brand]);
          }
        }
      });

      const chart = new Chart({
        container: container.current,
        autoFit: true,
        padding: 0,
        height: 310,
        theme: {
          colors10,
        },
      });
      updateChart(chart);
      setGraph(chart);
    }
  };

  useEffect(() => {
    if (d && d.length > 0) {
      if (!graph) {
        createChart();
      } else {
        updateChart(graph);
      }
    } else if (graph) {
      graph.destroy();
      setGraph(null);
      // updateChart(graph);
    }
  }, [d, type]);

  useEffect(() => {
    if (graph) {
      graph.forceFit();
    }
  }, [resizeTrigger]);

  return {
    container,
    graph,
  };
};
